import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchPlans,
  createPlan,
  updatePlan,
  deletePlan,
} from '../../store/pricingSlice';
import type { PricingPlan } from '../../types';

const PricingPlans: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { plans, loading, error } = useAppSelector((state) => state.pricing);
  const [open, setOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PricingPlan | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    start_date: '',
    end_date: '',
    is_active: false,
  });

  useEffect(() => {
    dispatch(fetchPlans());
  }, [dispatch]);

  const handleOpen = (plan?: PricingPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        code: plan.code,
        name: plan.name,
        start_date: plan.start_date || '',
        end_date: plan.end_date || '',
        is_active: plan.is_active,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        code: '',
        name: '',
        start_date: '',
        end_date: '',
        is_active: false,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPlan(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingPlan) {
        await dispatch(updatePlan({ id: editingPlan.id, data: formData })).unwrap();
      } else {
        await dispatch(createPlan(formData)).unwrap();
      }
      handleClose();
      dispatch(fetchPlans());
    } catch (err) {
      console.error('Error saving plan:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) {
      try {
        await dispatch(deletePlan(id)).unwrap();
        dispatch(fetchPlans());
      } catch (err) {
        console.error('Error deleting plan:', err);
      }
    }
  };

  const handleRowClick = (plan: PricingPlan) => {
    // Navigate to grid with selected plan
    navigate(`/pricing/grid?plan=${plan.id}`);
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        TARIFS
      </Typography>

      {/* Subtitle + Add Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Liste de vos tarifs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {plans.length} tarifs trouvés
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
        >
          + Ajouter un tarif
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1976D2' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Code</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Libellé</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actif</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Début</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Fin</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    Aucun tarif trouvé
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow
                    key={plan.id}
                    hover
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => handleRowClick(plan)}
                  >
                    <TableCell>{plan.code}</TableCell>
                    <TableCell>{plan.name}</TableCell>
                    <TableCell align="center">
                      <Checkbox checked={plan.is_active} disabled size="small" />
                    </TableCell>
                    <TableCell align="center">{plan.start_date || '-'}</TableCell>
                    <TableCell align="center">{plan.end_date || '-'}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ bgcolor: '#f44336', mr: 0.5, minWidth: 'auto', px: 1 }}
                        onClick={() => handleDelete(plan.id)}
                      >
                        delete
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        sx={{ bgcolor: '#9E9E9E', minWidth: 'auto', px: 1 }}
                        onClick={() => handleOpen(plan)}
                      >
                        edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* Empty rows for visual consistency */}
              {plans.length > 0 && plans.length < 5 && (
                Array.from({ length: 5 - plans.length }).map((_, idx) => (
                  <TableRow key={`empty-${idx}`}>
                    <TableCell colSpan={6} sx={{ py: 2 }}>&nbsp;</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
          {editingPlan ? 'Modifier un tarif' : 'Ajouter un tarif'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                size="small"
                fullWidth
                placeholder="Code"
              />
              <TextField
                label="Libellé"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                size="small"
                fullWidth
                placeholder="Libellé"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Début"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="Début"
              />
              <TextField
                label="Fin"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder="Fin"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                size="small"
              />
              <Typography variant="body2">Activer</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button onClick={handleClose}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.code || !formData.name}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingPlans;
